"use strict";

(() => {
  const STORAGE_KEY = "toy_topia_wishlist_products";

  function getStorefrontRoot() {
    const root =
      window.__toytopiaRoutes?.root || window.Shopify?.routes?.root || "/";

    return root.endsWith("/") ? root : `${root}/`;
  }

  function getCartAddUrl() {
    return (
      window.__toytopiaRoutes?.cartAdd ||
      window.Shopify?.routes?.cart_add_url ||
      "/cart/add.js"
    );
  }

  function onReady(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback);
      return;
    }

    callback();
  }

  function getWishlist() {
    try {
      const data = window.localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // eslint-disable-next-line no-console
      console.warn("Wishlist could not be saved");
    }
  }

  function isProductInWishlist(productId) {
    return getWishlist().some((item) => String(item.id) === String(productId));
  }

  function getWishlistI18n() {
    const i18n = window.__wishlistConfig?.i18n;

    return {
      add_to_wishlist: i18n?.add_to_wishlist || "Add to wishlist",
      remove_from_wishlist: i18n?.remove_from_wishlist || "Remove from wishlist",
      adding: i18n?.adding || "Adding…",
      buy_all: i18n?.buy_all || "Buy all",
    };
  }

  function updateWishlistButtons(scope = document) {
    const i18n = getWishlistI18n();

    scope.querySelectorAll("[data-wishlist-button]").forEach((button) => {
      const productId = button.dataset.productId;
      const isActive = isProductInWishlist(productId);

      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
      button.setAttribute(
        "aria-label",
        isActive ? i18n.remove_from_wishlist : i18n.add_to_wishlist
      );
    });
  }

  function updateWishlistCount(count) {
    const normalizedCount =
      typeof count === "number" ? count : getWishlist().length;

    document.querySelectorAll("[data-wishlist-count]").forEach((element) => {
      element.textContent = normalizedCount;

      if (normalizedCount > 0) {
        element.classList.remove("is-hidden");
      } else {
        element.classList.add("is-hidden");
      }
    });
  }

  function toggleWishlist(button) {
    const productId = button.dataset.productId;

    if (!productId) {
      return;
    }

    const wishlist = getWishlist();
    const existingIndex = wishlist.findIndex(
      (item) => String(item.id) === String(productId)
    );
    const willBeActive = existingIndex === -1;

    if (existingIndex > -1) {
      wishlist.splice(existingIndex, 1);
    } else {
      wishlist.push({
        id: String(productId),
        handle: button.dataset.productHandle || "",
        title: button.dataset.productTitle || "",
        url: button.dataset.productUrl || "",
      });
    }

    button.classList.toggle("is-active", willBeActive);
    button.setAttribute("aria-pressed", willBeActive ? "true" : "false");
    const i18n = getWishlistI18n();
    button.setAttribute(
      "aria-label",
      willBeActive ? i18n.remove_from_wishlist : i18n.add_to_wishlist
    );

    saveWishlist(wishlist);
    updateWishlistButtons(document);
    updateWishlistCount(wishlist.length);

    document.dispatchEvent(
      new CustomEvent("wishlist:updated", {
        detail: { wishlist },
      })
    );
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatMoney(cents) {
    const format = window.__wishlistConfig?.moneyFormat;

    if (typeof Shopify !== "undefined" && Shopify.formatMoney && format) {
      return Shopify.formatMoney(cents, format);
    }

    const currency = window.__wishlistConfig?.currency || "USD";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format((cents || 0) / 100);
  }

  function getBundledCartSectionPayload() {
    const ids = [];
    const drawer = document.querySelector("[data-cart-drawer-root]");

    if (drawer?.dataset.sectionId) {
      ids.push(drawer.dataset.sectionId);
    }

    const cartRoot = document.querySelector("[data-main-cart-root]");

    if (cartRoot?.dataset.sectionId) {
      ids.push(cartRoot.dataset.sectionId);
    }

    if (!ids.length) {
      return null;
    }

    const cartPath = window.__glowRoutes?.cartUrl || "/cart";
    const sectionsUrl = cartPath.startsWith("/") ? cartPath : `/${cartPath}`;

    return {
      sections: ids.join(","),
      sections_url: sectionsUrl,
    };
  }

  function applyBundledCartSections(result) {
    if (result?.sections && window.cartUi?.applyBundledSections) {
      window.cartUi.applyBundledSections(result.sections);
    }
  }

  async function fetchProductByHandle(handle) {
    const response = await fetch(
      `${getStorefrontRoot()}products/${encodeURIComponent(handle)}.js`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      }
    );

    if (!response.ok) {
      throw new Error("Product not found");
    }

    return response.json();
  }

  function getPreferredVariant(product) {
    return (
      product.variants.find((variant) => variant.available) ||
      product.variants[0] ||
      null
    );
  }

  function buildPriceHtml(product) {
    const hasSale =
      product.compare_at_price && product.compare_at_price > product.price;

    if (hasSale) {
      return `
        <span class="product-card__price-compare">${formatMoney(product.compare_at_price)}</span>
        <span class="product-card__price-current product-card__price-current--sale">${formatMoney(product.price)}</span>
      `;
    }

    return `
      <span class="product-card__price-current">${formatMoney(product.price)}</span>
    `;
  }

  function buildProductCardHtml(product) {
    const i18n = getWishlistI18n();
    const productUrl = `${getStorefrontRoot()}products/${product.handle}`;
    const image = product.featured_image || product.images?.[0] || "";
    const imageHtml = image
      ? `<img class="product-card__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="700" height="700">`
      : `<div class="product-card__placeholder"></div>`;

    return `
      <article
        class="product-card wishlist-page__card"
        data-product-card
        data-product-url="${escapeHtml(productUrl)}"
      >
        <div class="product-card__image-wrap">
          <a class="product-card__image-link" href="${escapeHtml(productUrl)}" aria-label="${escapeHtml(product.title)}">
            ${imageHtml}
          </a>
          <button
            class="product-card__wishlist is-active"
            type="button"
            aria-label="${escapeHtml(i18n.remove_from_wishlist)}"
            aria-pressed="true"
            data-wishlist-button
            data-product-id="${escapeHtml(product.id)}"
            data-product-handle="${escapeHtml(product.handle)}"
            data-product-title="${escapeHtml(product.title)}"
            data-product-url="${escapeHtml(productUrl)}"
          >
            <svg class="product-card__wishlist-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </button>
        </div>
        <a class="product-card__content" href="${escapeHtml(productUrl)}">
          <h3 class="product-card__title">${escapeHtml(product.title)}</h3>
          <div class="product-card__price">
            ${buildPriceHtml(product)}
          </div>
        </a>
      </article>
    `;
  }

  async function loadWishlistProducts() {
    const storedItems = getWishlist();

    if (!storedItems.length) {
      return [];
    }

    const results = await Promise.all(
      storedItems.map(async (item) => {
        if (!item.handle) {
          return { item, product: null };
        }

        try {
          const product = await fetchProductByHandle(item.handle);
          return { item, product };
        } catch {
          return { item, product: null };
        }
      })
    );

    const validProducts = [];
    const nextWishlist = [];

    results.forEach(({ product }) => {
      if (product) {
        validProducts.push(product);
        nextWishlist.push({
          id: String(product.id),
          handle: product.handle,
          title: product.title,
          url: `${getStorefrontRoot()}products/${product.handle}`,
        });
      }
    });

    if (nextWishlist.length !== storedItems.length) {
      saveWishlist(nextWishlist);
      document.dispatchEvent(
        new CustomEvent("wishlist:updated", {
          detail: { wishlist: nextWishlist },
        })
      );
    }

    return validProducts;
  }

  function setWishlistPageState(root, { isLoading, hasProducts }) {
    const emptyState = root.querySelector("[data-wishlist-empty]");
    const grid = root.querySelector("[data-wishlist-grid]");
    const buyAllButton = root.querySelector("[data-wishlist-buy-all]");

    if (emptyState) {
      emptyState.hidden = isLoading || hasProducts;
    }

    if (grid) {
      grid.hidden = isLoading || !hasProducts;
    }

    if (buyAllButton) {
      buyAllButton.disabled = isLoading || !hasProducts;
    }
  }

  async function renderWishlistPage(root) {
    const grid = root.querySelector("[data-wishlist-grid]");

    if (!grid) {
      return;
    }

    setWishlistPageState(root, { isLoading: true, hasProducts: false });
    grid.innerHTML = "";

    const products = await loadWishlistProducts();

    if (!products.length) {
      setWishlistPageState(root, { isLoading: false, hasProducts: false });
      return;
    }

    grid.innerHTML = products.map(buildProductCardHtml).join("");
    setWishlistPageState(root, { isLoading: false, hasProducts: true });
    updateWishlistButtons(grid);
  }

  async function addAllWishlistItemsToCart(root) {
    const buyAllButton = root.querySelector("[data-wishlist-buy-all]");

    if (!buyAllButton || buyAllButton.disabled) {
      return;
    }

    const i18n = getWishlistI18n();
    const initialText = buyAllButton.textContent;
    buyAllButton.disabled = true;
    buyAllButton.textContent = i18n.adding;

    try {
      const products = await loadWishlistProducts();
      const items = products
        .map((product) => {
          const variant = getPreferredVariant(product);

          if (!variant?.available) {
            return null;
          }

          return {
            id: Number(variant.id),
            quantity: 1,
          };
        })
        .filter(Boolean);

      if (!items.length) {
        return;
      }

      const response = await fetch(getCartAddUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items,
          ...(getBundledCartSectionPayload() || {}),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add items to cart");
      }

      const data = await response.json();
      applyBundledCartSections(data);

      document.dispatchEvent(
        new CustomEvent("cart:refresh", {
          detail: { openDrawer: true },
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Wishlist buy all error:", error);
    } finally {
      buyAllButton.textContent = initialText;
      setWishlistPageState(root, {
        isLoading: false,
        hasProducts: Boolean(root.querySelector("[data-wishlist-grid]")?.children.length),
      });
    }
  }

  function initWishlistPage(scope = document) {
    const root = scope.querySelector("[data-wishlist-page]");

    if (!root || root.dataset.wishlistInitialized === "true") {
      return;
    }

    root.dataset.wishlistInitialized = "true";

    const buyAllButton = root.querySelector("[data-wishlist-buy-all]");

    buyAllButton?.addEventListener("click", () => {
      addAllWishlistItemsToCart(root);
    });

    renderWishlistPage(root);
  }

  document.addEventListener(
    "click",
    (event) => {
      const wishlistButton = event.target.closest("[data-wishlist-button]");

      if (!wishlistButton) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(wishlistButton);
    },
    true
  );

  document.addEventListener("wishlist:updated", (event) => {
    updateWishlistCount(event.detail?.wishlist?.length);

    const root = document.querySelector("[data-wishlist-page]");

    if (root) {
      renderWishlistPage(root);
    }
  });

  window.wishlistUi = {
    syncButtons(scope = document) {
      updateWishlistButtons(scope);
      updateWishlistCount();
    },
  };

  onReady(() => {
    initWishlistPage();
    updateWishlistButtons(document);
    updateWishlistCount();
  });

  document.addEventListener("shopify:section:load", (event) => {
    initWishlistPage(event.target);
    updateWishlistButtons(event.target);
    updateWishlistCount();
  });
})();
